import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { Status } from 'src/models/AuthContextModels';
import {
  CorpSegmentFilterSelection,
  ForecastGridRowData,
  ForecastTemplateCorpSegmentDropdowns,
  ForecastTemplateDataState,
  ForecastTemplateDataValidationStatus
} from 'src/models/ForecastModels';
import { PlanningCycleEntity } from 'src/models/PlanningCycleModel';
import { ForecastTemplateDataStatuses } from '../forecast-utils/ForecastGridConstants';
import { INITIAL_VALIDATION_STATUS } from '../services';

export interface ForecastTemplateState {
  currentPlanningCycle: PlanningCycleEntity | null;
  currentCorpSegmentDropDownSelections: CorpSegmentFilterSelection | null;

  forecastTemplateDataLoading: boolean;
  forecastTemplateCompleteData: ForecastGridRowData[];
  forecastTemplateCorpSegmentDropdowns: ForecastTemplateCorpSegmentDropdowns[];

  forecastDataStatus: ForecastTemplateDataState;
  forecastDataSubmitClickCount: number;
  forecastDataSubmitting: boolean;
  forecastDataSubmitError: string;
  forecastDataValidationStatus: ForecastTemplateDataValidationStatus;
}

export const ForecastTemplateInitialState: ForecastTemplateState = {
  currentPlanningCycle: null,
  currentCorpSegmentDropDownSelections: null,

  forecastTemplateDataLoading: false,
  forecastTemplateCompleteData: [],
  forecastTemplateCorpSegmentDropdowns: [],

  forecastDataStatus: {
    dataStatus: ForecastTemplateDataStatuses.NotLoaded // Data is 'NotLoaded' at this point of time.
  },
  forecastDataSubmitClickCount: 0,
  forecastDataSubmitting: false,
  forecastDataSubmitError: '',
  forecastDataValidationStatus: INITIAL_VALIDATION_STATUS
};

const forecastTemplateSlice = createSlice({
  name: 'forecastTemplate',
  initialState: ForecastTemplateInitialState,
  reducers: {
    setCurrentPlanningCycle: (state, action: PayloadAction<PlanningCycleEntity | null>) => {
      state.currentPlanningCycle = action.payload;
    },
    setCurrentCorpSegmentDropDownSelections: (state, action: PayloadAction<CorpSegmentFilterSelection | null>) => {
      state.currentCorpSegmentDropDownSelections = action.payload as Draft<CorpSegmentFilterSelection>;
    },

    setForecastTemplateCompleteData: (state, action: PayloadAction<any[]>) => {
      state.forecastTemplateCompleteData = action.payload;
    },
    setForecastTemplateCorpSegmentDropdowns: (state, action: PayloadAction<ForecastTemplateCorpSegmentDropdowns[]>) => {
      state.forecastTemplateCorpSegmentDropdowns = action.payload as Draft<ForecastTemplateCorpSegmentDropdowns[]>;
    },
    setForecastTemplateDataStatus: (state, action: PayloadAction<ForecastTemplateDataState>) => {
      state.forecastDataStatus = action.payload;
    },
    setForecastTemplateDataLoading: (state, action: PayloadAction<boolean>) => {
      state.forecastTemplateDataLoading = action.payload;
    },
    setForecastDataSubmitClickCount: (state, action: PayloadAction<number>) => {
      state.forecastDataSubmitClickCount = action.payload;
    },
    setForecastDataSubmitting: (state, action: PayloadAction<boolean>) => {
      state.forecastDataSubmitting = action.payload;
    },
    setForecastDataSubmitError: (state, action: PayloadAction<string>) => {
      state.forecastDataSubmitError = action.payload;
    },
    setValidationStatus: (state, action: PayloadAction<ForecastTemplateDataValidationStatus>) => {
      state.forecastDataValidationStatus = action.payload;
    }
  }
});

export const {
  setCurrentPlanningCycle,
  setCurrentCorpSegmentDropDownSelections,

  setForecastTemplateCompleteData,
  setForecastTemplateCorpSegmentDropdowns,
  setForecastTemplateDataStatus,
  setForecastTemplateDataLoading,
  setForecastDataSubmitClickCount,
  setForecastDataSubmitting,
  setForecastDataSubmitError,
  setValidationStatus
} = forecastTemplateSlice.actions;

export default forecastTemplateSlice.reducer;
